/**
 * Event Tracker Service
 * Tracks user interactions (clicks, searches, navigation, etc.) and stores counts in memory
 */

interface EventData {
    count: number;
    metadata?: Record<string, unknown>;
}

class EventTracker {
    private static instance: EventTracker;
    private eventData: Map<string, EventData> = new Map();
    private eventOrder: string[] = [];
    private maxEventOrderSize: number = 100; // Limit to prevent memory issues

    private constructor() {}

    public static getInstance(): EventTracker {
        if (!EventTracker.instance) {
            EventTracker.instance = new EventTracker();
        }
        return EventTracker.instance;
    }

    /**
     * Track an event with optional metadata
     * @param eventKey - The event identifier (e.g., 'nav_tab_ALL_GAMES', 'search_query_poker')
     * @param metadata - Optional additional data about the event
     */
    public trackEvent(eventKey: string, metadata?: Record<string, unknown>): void {
        const existing = this.eventData.get(eventKey);

        if (existing) {
            existing.count++;
            // Update metadata if provided
            if (metadata) {
                existing.metadata = { ...existing.metadata, ...metadata };
            }
        } else {
            this.eventData.set(eventKey, {
                count: 1,
                metadata
            });
        }

        // Add to event order
        this.eventOrder.push(eventKey);

        // Trim event order if it exceeds max size
        if (this.eventOrder.length > this.maxEventOrderSize) {
            this.eventOrder.shift(); // Remove oldest event
        }

        if (process.env['NODE_ENV'] === 'development') {
            const current = this.eventData.get(eventKey);
            if (current) {
                console.log(`[EventTracker] ${eventKey}: ${current.count}`, current.metadata);
            }
        }
    }

    /**
     * Backwards compatibility alias for trackEvent
     * @deprecated Use trackEvent instead
     */
    public incrementClick(eventKey: string, metadata?: Record<string, unknown>): void {
        this.trackEvent(eventKey, metadata);
    }

    /**
     * Track event in order only (without counting)
     * Use this for events where you want to track the sequence but not aggregate counts
     * @param eventKey - The event identifier
     */
    public trackEventOrderOnly(eventKey: string): void {
        this.eventOrder.push(eventKey);

        // Trim event order if it exceeds max size
        if (this.eventOrder.length > this.maxEventOrderSize) {
            this.eventOrder.shift();
        }

        if (process.env['NODE_ENV'] === 'development') {
            console.log(`[EventTracker] Order only: ${eventKey}`);
        }
    }

    /**
     * Get all event counts formatted for Sentry
     */
    public getFormattedCounts(): Record<string, string | number | boolean | string[]> {
        const formatted: Record<string, string | number | boolean | string[]> = {};

        this.eventData.forEach((data, eventKey) => {
            // Create Sentry key format - using 'interactions' instead of 'clicks'
            formatted[`egt.lobby.ui.interactions.${eventKey}`] = data.count;

            // Add metadata as separate attributes if exists
            if (data.metadata) {
                Object.entries(data.metadata).forEach(([metaKey, metaValue]) => {
                    if (typeof metaValue === 'string' || typeof metaValue === 'number' || typeof metaValue === 'boolean') {
                        formatted[`egt.lobby.ui.interactions.${eventKey}_${metaKey}`] = metaValue;
                    }
                });
            }
        });

        // Add total if there are any events
        if (this.eventData.size > 0) {
            const total = Array.from(this.eventData.values()).reduce((sum, data) => sum + data.count, 0);
            formatted['egt.lobby.ui.interactions.total'] = total;
        }

        // Add interactions order at the same level as interactions (last 100 events for Sentry)
        if (this.eventOrder.length > 0) {
            const recentEvents = this.eventOrder.slice(-100); // Last 100 events
            formatted['egt.lobby.ui.interactions.interactions_order'] = recentEvents.join(',');
        }

        return formatted;
    }

    /**
     * Clear all events (useful for new sessions)
     */
    public clear(): void {
        this.eventData.clear();
        this.eventOrder = [];
    }

    /**
     * Get count for specific event (for debugging)
     */
    public getCount(eventKey: string): number {
        return this.eventData.get(eventKey)?.count || 0;
    }

    /**
     * Get event order (for debugging)
     */
    public getEventOrder(): string[] {
        return [...this.eventOrder];
    }
}

// Export with the new name
export const eventTracker = EventTracker.getInstance();

// Export with old name for backwards compatibility
// @deprecated Use eventTracker instead
export const clickCounter = eventTracker;